/**
 * @fileoverview User Management configuration query hook.
 * @description Wraps GET /users/configuration for the Deactivate and Reactivate dialogs: the
 * lifecycle reasons to choose from (each a stable code and a label) and the longest a reason note
 * may be. The reasons are fixed metadata, so they are read once and kept.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import { getUserManagementConfiguration } from '@/features/users/services/users.service'

import type { UserManagementConfiguration } from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

const ONE_HOUR_MS = 60 * 60 * 1000

/**
 * @description Fetches the lifecycle reasons and limits.
 * @returns {import('@tanstack/react-query').UseQueryResult<UserManagementConfiguration, ApiError>}
 * The query result.
 */
export function useUserManagementConfiguration() {
  return useQuery<UserManagementConfiguration, ApiError>({
    queryKey: USERS_QUERY_KEYS.configuration,
    queryFn: getUserManagementConfiguration,
    staleTime: ONE_HOUR_MS,
  })
}
